import isString from 'lodash-es/isString';
import { BrowsingContextError } from '../../browsingContextError';
import { isEmptyIterable } from '../iterable';
import { removeSelectionBookmarkAttributesFromElement } from '../selectionBookmarks';
import {
  getNodeContents,
  getNodesIntersectingRange,
  isElement,
  isElementWithTagName,
  isHTMLOrSVGElement,
  unwrapNode,
} from './node';
import { createPointWithEndOfRange, createPointWithStartOfRange, createRangeWithPoints } from './point';

export const INLINE_DISPLAY_VALUES = new Set([
  'inline',
  'inline-block',
  'inline-flex',
  'inline-grid',
  'inline-table',
]);

export function getComputedStyle(element: Element) {
  const window = element.ownerDocument && element.ownerDocument.defaultView;
  if (!window) {
    return null;
  }

  return window.getComputedStyle(element);
}

export function isInline(element: Element): boolean {
  return isHTMLOrSVGElement(element) && INLINE_DISPLAY_VALUES.has(element.style.display);
}

export function getElementContents(element: Element): DocumentFragment {
  return getNodeContents(element);
}

export function unwrapElement(element: Element) {
  unwrapNode(element);
}

export function setElementTagName<T extends Element, K extends keyof HTMLElementTagNameMap>(
  element: T,
  tagName: K,
): T | HTMLElementTagNameMap[K] {
  if (isElementWithTagName(element, tagName)) {
    return element;
  }

  const { ownerDocument: document, parentNode } = element;
  if (!document || !parentNode) {
    return element;
  }

  const newElement = document.createElement(tagName);
  setAttributesFromElement(newElement, element);
  newElement.appendChild(getElementContents(element));
  parentNode.insertBefore(newElement, element);
  parentNode.removeChild(element);
  return newElement;
}

export function setAttributesFromElement(element: Element, sourceElement: Element) {
  const { attributes } = sourceElement;
  for (let i = 0; i < attributes.length; i++) {
    const attribute = attributes[i];
    element.setAttribute(attribute.name, attribute.value);
  }
}

export function simplifyAttributes(element: Element) {
  // Remove empty style attributes.
  const style = element.getAttribute('style');
  if (isString(style) && style.trim() === '') {
    element.removeAttribute('style');
  }

  // Remove selection bookmark attributes.
  // TODO: Is this the best place for this?
  removeSelectionBookmarkAttributesFromElement(element);
}

export function simplifyElement(element: Element): Node | null {
  const document = element.ownerDocument;
  if (!document) {
    throw new BrowsingContextError();
  }

  simplifyAttributes(element);

  // Spans with attributes can't be simplified any further.
  const { attributes } = element;
  if (element.tagName === 'SPAN' && attributes.length > 0) {
    return element;
  }

  let replacement: Node;

  // Convert non-spans to a span, if there are attributes.
  if (attributes.length > 0) {
    const span = document.createElement('span');

    // Copy the attributes.
    for (let i = 0; i < attributes.length; i++) {
      const { name, value } = attributes[i];
      span.setAttribute(name, value);
    }

    replacement = span;
  } else {
    replacement = document.createDocumentFragment();
  }

  // Transplant the children.
  let childNode: Node | null = element.firstChild;
  while (childNode) {
    const nextChildNode = childNode.nextSibling;
    replacement.appendChild(childNode);
    childNode = nextChildNode;
  }

  return replacement;
}

export function hasElementsIntersectingRange(
  container: Node,
  range: Range,
  condition: (element: Element) => boolean,
): boolean {
  return !isEmptyIterable(getElementsIntersectingRange(container, range, condition));
}

export function getElementsIntersectingRange<T extends Element>(
  container: Node,
  range: Range,
  condition: (element: Element) => element is T,
  strictMode?: boolean,
): Iterable<T>;
export function getElementsIntersectingRange(
  container: Node,
  range: Range,
  condition: (element: Element) => boolean,
  strictMode?: boolean,
): Iterable<Element>;
export function getElementsIntersectingRange(
  container: Node,
  range: Range,
  condition: (element: Element) => boolean,
  strictMode?: boolean,
): Iterable<Element> {
  const elementCondition = (node => isElement(node) && condition(node)) as typeof isElement;
  return getNodesIntersectingRange(container, range, elementCondition, strictMode);
}

export function mutateElementsIntersectingRange<T extends Element>(
  container: Node,
  range: Range,
  condition: (element: Element) => element is T,
  callback: (elements: T[]) => void,
  strictMode?: boolean,
): Range;
export function mutateElementsIntersectingRange<T extends Element>(
  container: Node,
  range: Range,
  condition: (element: Element) => boolean,
  callback: (elements: Element[]) => void,
  strictMode?: boolean,
): Range;
export function mutateElementsIntersectingRange(
  container: Node,
  range: Range,
  condition: (element: Element) => boolean,
  callback: (elements: Element[]) => void,
  strictMode?: boolean,
): Range {
  const document = container.ownerDocument;
  if (!document) {
    return range;
  }

  // Save the range in a way that's stable under DOM mutations.
  const startPoint = createPointWithStartOfRange(range);
  const endPoint = createPointWithEndOfRange(range);

  callback(Array.from(getElementsIntersectingRange(container, range, condition, strictMode)));

  // Restore the range.
  return createRangeWithPoints(document, startPoint, endPoint);
}

export function scrollElementIntoViewIfNeeded(element: Element): void {
  const window = element.ownerDocument && element.ownerDocument.defaultView;
  if (!window) {
    return;
  }

  // If the bottom is below the top of the viewport, and the top is below the bottom of the viewport, no need to scroll.
  const clientRect = element.getBoundingClientRect();
  if (clientRect.bottom > 0 && clientRect.top < window.innerHeight) {
    return;
  }

  element.scrollIntoView();
}
